(function() {
  'use strict';

  var MIN_CHARS = 2;
  var PAGE_LIMIT = 250;
  var MAX_PAGES = 48;
  var catalogCache = {};
  var catalogLoading = {};
  var skuIndexCache = {};
  var catalogFullyLoaded = {};

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function normalizeQuery(q) {
    return String(q || '').trim().toLowerCase();
  }

  // "23114 OR B23114" -> "23114"
  function primaryQuery(q) {
    return normalizeQuery(q).split(/\s+or\s+/i)[0].trim();
  }

  // B217904Y -> 217904y (letter prefix optional in search)
  function skuCore(sku) {
    var s = String(sku || '').trim().toLowerCase();
    if (/^[a-z]\d/i.test(s)) return s.slice(1);
    return s;
  }

  // B23145Y / 23145y / B23145 -> "23145"
  function digitCore(sku) {
    var m = String(sku || '').toLowerCase().match(/\d{2,}/);
    return m ? m[0] : '';
  }

  function expandSkuQueries(query) {
    var raw = String(query || '').trim().split(/\s+or\s+/i)[0].trim();
    var list = raw ? [raw] : [];
    var seen = {};
    if (raw) seen[raw.toLowerCase()] = true;

    function add(term) {
      var key = String(term || '').toLowerCase();
      if (!term || seen[key]) return;
      seen[key] = true;
      list.push(term);
    }

    var lower = raw.toLowerCase();
    if (/^\d/.test(raw)) {
      add('B' + raw);
      add('b' + raw);
    }
    if (/^[a-z]\d/i.test(raw)) {
      add(raw.slice(1));
    }
    if (/[a-z]$/i.test(raw) && /\d/.test(raw)) {
      add(raw.slice(0, -1));
      if (/^[a-z]/i.test(raw)) add(raw.slice(1, -1));
    }
    var core = skuCore(lower);
    if (core && core !== lower) {
      add(core);
      add('B' + core);
    }
    var digits = digitCore(lower);
    if (digits) {
      add(digits);
      add('B' + digits);
    }
    return list;
  }

  function shopifySkuSearchQuery(query) {
    var parts = expandSkuQueries(query);
    if (parts.length === 1) return parts[0];
    return parts.join(' OR ');
  }

  function productSkus(product) {
    return (product.variants || [])
      .map(function(v) { return String(v.sku || '').trim().toLowerCase(); })
      .filter(Boolean);
  }

  function productHaystack(product) {
    var skus = productSkus(product);
    var parts = [
      product.title || '',
      product.vendor || '',
      product.product_type || '',
      product.handle || '',
      (product.tags || []).join(' '),
      skus.join(' '),
      skus.map(skuCore).join(' '),
      skus.map(digitCore).join(' ')
    ];
    return parts.join(' ').toLowerCase();
  }

  function matchesSkuPartial(product, query) {
    var q = primaryQuery(query);
    if (q.length < MIN_CHARS) return false;
    var qCore = skuCore(q);
    var qDigits = digitCore(q);
    return productSkus(product).some(function(sku) {
      if (sku === q || skuCore(sku) === q || skuCore(sku) === qCore) return true;
      if (sku.indexOf(q) !== -1) return true;
      if (skuCore(sku).indexOf(q) !== -1) return true;
      if (qCore && sku.indexOf(qCore) !== -1) return true;
      if (qCore && skuCore(sku).indexOf(qCore) !== -1) return true;
      var skuDigits = digitCore(sku);
      if (qDigits && skuDigits && qDigits.length >= 2 && skuDigits.indexOf(qDigits) !== -1) return true;
      return false;
    });
  }

  function matchesQuery(product, query) {
    var q = primaryQuery(query);
    if (q.length < MIN_CHARS) return false;
    if (matchesSkuPartial(product, q)) return true;
    var words = q.split(/\s+/).filter(function(word) {
      return word && word !== 'or';
    });
    if (!words.length) return false;
    var hay = productHaystack(product);
    return words.every(function(word) {
      return hay.indexOf(word) !== -1;
    });
  }

  function fetchCollectionPage(handle, page) {
    return fetch(root() + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_LIMIT + '&page=' + page, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function(res) {
      if (!res.ok) throw new Error('fetch failed');
      return res.json();
    });
  }

  function buildSkuIndex(products) {
    var index = [];
    (products || []).forEach(function(product) {
      (product.variants || []).forEach(function(variant) {
        var sku = String(variant.sku || '').trim().toLowerCase();
        if (!sku) return;
        index.push({ sku: sku, product: product });
      });
    });
    return index;
  }

  function appendCatalogProducts(handle, products) {
    if (!products.length) return catalogCache[handle] || [];
    var existing = catalogCache[handle] || [];
    var seen = {};
    existing.forEach(function(product) {
      seen[String(product.id || '')] = true;
    });
    products.forEach(function(product) {
      var id = String(product.id || '');
      if (!id || seen[id]) return;
      seen[id] = true;
      existing.push(product);
    });
    catalogCache[handle] = existing;
    skuIndexCache[handle] = buildSkuIndex(existing);
    return existing;
  }

  function loadRemainingPages(handle, page, acc) {
    return fetchCollectionPage(handle, page)
      .then(function(data) {
        var products = data.products || [];
        appendCatalogProducts(handle, products);
        if (!products.length || products.length < PAGE_LIMIT || page >= MAX_PAGES) {
          catalogFullyLoaded[handle] = true;
          return catalogCache[handle] || acc;
        }
        return loadRemainingPages(handle, page + 1, catalogCache[handle] || acc);
      })
      .catch(function() {
        catalogFullyLoaded[handle] = true;
        return catalogCache[handle] || acc;
      });
  }

  function loadCatalog(handle) {
    handle = resolveCatalogHandle(handle);
    if (catalogFullyLoaded[handle] && catalogCache[handle]) {
      return Promise.resolve(catalogCache[handle]);
    }
    if (catalogLoading[handle]) return catalogLoading[handle];

    catalogLoading[handle] = fetchCollectionPage(handle, 1)
      .then(function(data) {
        var products = data.products || [];
        appendCatalogProducts(handle, products);
        if (products.length >= PAGE_LIMIT && pageNeedsMore(products)) {
          loadRemainingPages(handle, 2, catalogCache[handle]).catch(function() {});
        } else {
          catalogFullyLoaded[handle] = true;
        }
        delete catalogLoading[handle];
        return catalogCache[handle] || [];
      })
      .catch(function() {
        delete catalogLoading[handle];
        catalogCache[handle] = catalogCache[handle] || [];
        catalogFullyLoaded[handle] = true;
        return catalogCache[handle];
      });

    return catalogLoading[handle];
  }

  function pageNeedsMore(products) {
    return products.length >= PAGE_LIMIT;
  }

  function resolveCatalogHandle(preferred) {
    var handle = String(preferred || '').trim();
    if (handle) return handle;
    return 'all-jewelry';
  }

  function fetchShopifySearchProducts(query, limit) {
    var terms = expandSkuQueries(query);
    var fetches = terms.map(function(term) {
      var params = new URLSearchParams();
      params.set('q', term);
      params.set('type', 'product');
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(limit || 8));

      return fetch(root() + 'search/suggest.json?' + params.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function(res) {
          if (!res.ok) throw new Error('search failed');
          return res.json();
        })
        .then(function(data) {
          var products = (((data || {}).resources || {}).results || {}).products || [];
          return products.map(normalizeSuggestProduct);
        })
        .catch(function() {
          return [];
        });
    });

    return Promise.all(fetches).then(function(lists) {
      var merged = [];
      lists.forEach(function(list) {
        merged = mergeProductResults(merged, list, limit || 8);
      });
      return merged;
    });
  }

  function runIndexedSkuSearch(index, query, limit) {
    var q = primaryQuery(query);
    var qCore = skuCore(q);
    var results = [];
    var seen = {};
    for (var i = 0; i < index.length; i++) {
      var sku = index[i].sku;
      var core = skuCore(sku);
      var skuDigits = digitCore(sku);
      var qDigits = digitCore(q);
      var hit =
        sku.indexOf(q) !== -1 ||
        core.indexOf(q) !== -1 ||
        (qCore && (sku.indexOf(qCore) !== -1 || core.indexOf(qCore) !== -1)) ||
        (qDigits && skuDigits && skuDigits.indexOf(qDigits) !== -1);
      if (!hit) {
        continue;
      }
      var id = String(index[i].product.id || '');
      if (!id || seen[id]) continue;
      seen[id] = true;
      results.push(index[i].product);
      if (limit && results.length >= limit) break;
    }
    return results;
  }

  function runCatalogSearch(catalog, query, limit) {
    var q = normalizeQuery(query);
    var skuMatches = [];
    var otherMatches = [];
    var seen = {};

    catalog.forEach(function(product) {
      var id = String(product.id || '');
      if (!id || seen[id]) return;
      if (matchesSkuPartial(product, q)) {
        seen[id] = true;
        skuMatches.push(product);
      }
    });

    catalog.forEach(function(product) {
      var id = String(product.id || '');
      if (!id || seen[id]) return;
      if (matchesQuery(product, q)) {
        seen[id] = true;
        otherMatches.push(product);
      }
    });

    var results = skuMatches.concat(otherMatches);
    if (limit && limit > 0) return results.slice(0, limit);
    return results;
  }

  function normalizeSuggestProduct(product) {
    var handle = product.handle || '';
    if (!handle && product.url) {
      var match = String(product.url).match(/\/products\/([^/?#]+)/);
      if (match) handle = match[1];
    }

    var imageSrc = '';
    if (product.featured_image && product.featured_image.url) {
      imageSrc = product.featured_image.url;
    } else if (product.image) {
      imageSrc = product.image;
    }

    var price = parseFloat(product.price_min || product.price || 0);
    if (Number.isNaN(price)) price = 0;

    return {
      id: product.id,
      title: product.title || '',
      handle: handle,
      images: imageSrc ? [{ src: imageSrc }] : [],
      variants: [{ price: price, sku: '' }]
    };
  }

  function mergeProductResults(primary, secondary, limit) {
    var merged = [];
    var seen = {};

    function pushList(list) {
      (list || []).forEach(function(product) {
        var id = String(product.id || '');
        var handle = String(product.handle || '');
        var key = id || handle;
        if (!key || seen[key]) return;
        seen[key] = true;
        merged.push(product);
      });
    }

    pushList(primary);
    pushList(secondary);

    if (limit && limit > 0) return merged.slice(0, limit);
    return merged;
  }

  function searchLocalCatalog(handle, query, limit) {
    var index = skuIndexCache[handle];
    if (index && index.length) {
      var indexed = runIndexedSkuSearch(index, query, limit);
      if (indexed.length) return indexed;
    }

    var catalog = catalogCache[handle];
    if (catalog && catalog.length) {
      return runCatalogSearch(catalog, query, limit);
    }

    return [];
  }

  function searchSkuOnly(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    var limit = options.limit || 8;
    var q = normalizeQuery(query);
    if (q.length < MIN_CHARS) return Promise.resolve([]);

    var cachedIndex = skuIndexCache[handle];
    if (cachedIndex && cachedIndex.length) {
      var cachedMatches = runIndexedSkuSearch(cachedIndex, q, limit);
      if (cachedMatches.length) return Promise.resolve(cachedMatches);
    }

    var cachedCatalog = catalogCache[handle];
    if (cachedCatalog && cachedCatalog.length) {
      var catalogMatches = cachedCatalog.filter(function(product) {
        return matchesSkuPartial(product, q);
      });
      if (catalogMatches.length) {
        return Promise.resolve(catalogMatches.slice(0, limit));
      }
    }

    // Wait for full catalog when query looks like a SKU (digit / letter+digit)
    var looksLikeSku = /^\d/.test(q) || /^[a-z]\d/i.test(q);
    var catalogPromise = looksLikeSku
      ? loadCatalogFully(handle)
      : loadCatalog(handle);

    return Promise.all([
      catalogPromise,
      fetchShopifySearchProducts(query, limit)
    ]).then(function(results) {
      var catalog = results[0] || [];
      var shopifyHits = results[1] || [];
      var index = skuIndexCache[handle] || buildSkuIndex(catalog);
      var indexed = runIndexedSkuSearch(index, q, limit);
      if (!indexed.length) {
        indexed = catalog.filter(function(product) {
          return matchesSkuPartial(product, q);
        }).slice(0, limit);
      }
      return mergeProductResults(indexed, shopifyHits, limit);
    });
  }

  function loadCatalogFully(handle) {
    handle = resolveCatalogHandle(handle);
    if (catalogFullyLoaded[handle] && catalogCache[handle]) {
      return Promise.resolve(catalogCache[handle]);
    }
    return loadCatalog(handle).then(function(firstPage) {
      if (catalogFullyLoaded[handle]) return catalogCache[handle] || firstPage || [];
      return loadRemainingPages(handle, 2, catalogCache[handle] || firstPage || []);
    });
  }

  function searchCatalog(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    var limit = options.limit || 8;
    var q = normalizeQuery(query);
    if (q.length < MIN_CHARS) return Promise.resolve([]);

    var looksLikeSku = /^\d/.test(q) || /^[a-z]\d/i.test(q);
    var localResults = searchLocalCatalog(handle, q, limit);
    var suggestPromise = fetchShopifySearchProducts(query, limit);
    var catalogPromise;

    if (localResults.length >= limit) {
      return suggestPromise.then(function(suggestProducts) {
        return mergeProductResults(localResults, suggestProducts, limit);
      });
    }

    if (looksLikeSku && !localResults.length) {
      catalogPromise = loadCatalogFully(handle);
    } else if (catalogCache[handle]) {
      catalogPromise = Promise.resolve(catalogCache[handle]);
    } else {
      catalogPromise = loadCatalog(handle);
    }

    return Promise.all([catalogPromise, suggestPromise]).then(function(results) {
      var catalog = results[0] || [];
      var suggestProducts = results[1] || [];
      var catalogMatches = localResults.length
        ? localResults
        : runCatalogSearch(catalog, q, limit);
      return mergeProductResults(catalogMatches, suggestProducts, limit);
    });
  }

  function searchProducts(query, options) {
    options = options || {};
    if (!options.limit) options.limit = 8;
    return searchCatalog(query, options);
  }

  function searchAllProducts(query, options) {
    return searchCatalog(query, options);
  }

  function getPrimarySku(product, query) {
    var variants = product.variants || [];
    var q = normalizeQuery(query);
    var qCore = skuCore(q);
    if (q) {
      for (var i = 0; i < variants.length; i++) {
        var sku = String(variants[i].sku || '').trim();
        if (!sku) continue;
        var skuLower = sku.toLowerCase();
        var core = skuCore(skuLower);
        if (
          skuLower === q ||
          core === qCore ||
          skuLower.indexOf(q) !== -1 ||
          core.indexOf(q) !== -1 ||
          (qCore && skuLower.indexOf(qCore) !== -1) ||
          (qCore && core.indexOf(qCore) !== -1)
        ) {
          return sku;
        }
      }
    }
    for (var j = 0; j < variants.length; j++) {
      if (variants[j].sku) return variants[j].sku;
    }
    return '';
  }

  function buildSpsProductItems(products, query) {
    if (!products.length) return '';
    var html = '<p class="sps-heading">Products</p><ul class="sps-list" role="listbox">';
    products.forEach(function(product) {
      var img = product.images && product.images[0] ? product.images[0].src : '';
      var variant = product.variants && product.variants[0] ? product.variants[0] : null;
      var sku = getPrimarySku(product, query);
      var priceCents = variant ? Math.round(Number(variant.price) * 100) : 0;
      var url = root() + 'products/' + product.handle;

      html += '<li role="option"><a href="' + url + '" class="sps-item">';
      if (img) {
        html += '<img src="' + escapeHtml(img) + '" alt="" class="sps-thumb" width="48" height="48" loading="lazy">';
      } else {
        html += '<span class="sps-thumb sps-thumb--empty" aria-hidden="true"></span>';
      }
      html += '<span class="sps-meta">';
      html += '<span class="sps-title">' + escapeHtml(product.title) + '</span>';
      if (sku) html += '<span class="sps-sku">SKU: ' + escapeHtml(sku) + '</span>';
      html += '<span class="sps-price">' + formatMoney(priceCents) + '</span>';
      html += '</span></a></li>';
    });
    html += '</ul>';
    return html;
  }

  function buildViewAllLink(query) {
    return (
      '<a href="' + root() + 'search?q=' + encodeURIComponent(query) + '&type=product" class="sps-view-all">' +
      'View all results for &ldquo;' + escapeHtml(query) + '&rdquo;</a>'
    );
  }

  function stripPredictiveProducts(html) {
    if (!html) return '';
    var doc = new DOMParser().parseFromString('<div id="sps-wrap">' + html + '</div>', 'text/html');
    var wrap = doc.getElementById('sps-wrap');
    if (!wrap) return html;

    wrap.querySelectorAll('.sps-heading').forEach(function(heading) {
      if ((heading.textContent || '').trim() !== 'Products') return;
      var next = heading.nextElementSibling;
      heading.remove();
      if (next && next.classList.contains('sps-list')) next.remove();
    });

    wrap.querySelectorAll('.sps-view-all').forEach(function(el) { el.remove(); });
    wrap.querySelectorAll('.sps-no-results').forEach(function(el) { el.remove(); });
    return wrap.innerHTML;
  }

  function mergeSpsDropdown(predictiveInnerHtml, skuProducts, query) {
    if (!skuProducts.length) {
      return predictiveInnerHtml
        ? '<div class="sps-dropdown-inner">' + predictiveInnerHtml + '</div>'
        : '';
    }

    var extra = stripPredictiveProducts(predictiveInnerHtml);
    var productsHtml = buildSpsProductItems(skuProducts, query);

    if (!productsHtml && !extra) {
      return '<div class="sps-dropdown-inner"><p class="sps-no-results">No results found. Try product name or SKU (2+ characters).</p></div>';
    }

    return '<div class="sps-dropdown-inner">' + productsHtml + extra + buildViewAllLink(query) + '</div>';
  }

  function buildPredictiveSkuGrid(products, query) {
    if (!products.length) return '';
    var html = '<div class="predictive-search__sku-fallback">';
    html += '<p class="predictive-search__title h5">Products</p>';
    html += '<div class="predictive-search__grid predictive-search__grid--products" data-grid-id="products">';
    products.forEach(function(product, index) {
      var img = product.images && product.images[0] ? product.images[0].src : '';
      var variant = product.variants && product.variants[0] ? product.variants[0] : null;
      var sku = getPrimarySku(product, query);
      var priceCents = variant ? Math.round(Number(variant.price) * 100) : 0;
      var url = root() + 'products/' + product.handle;

      html += '<div class="predictive-search__grid-item predictive-search__sku-item" data-animate data-animate-order="' + (index + 1) + '">';
      html += '<a href="' + url + '" class="predictive-search__sku-link">';
      if (img) {
        html += '<img src="' + escapeHtml(img) + '" alt="" class="predictive-search__sku-image" loading="lazy" width="120" height="120">';
      }
      html += '<div class="predictive-search__sku-meta">';
      html += '<span class="predictive-search__sku-title">' + escapeHtml(product.title) + '</span>';
      if (sku) html += '<span class="predictive-search__sku-code">SKU: ' + escapeHtml(sku) + '</span>';
      html += '<span class="predictive-search__sku-price">' + formatMoney(priceCents) + '</span>';
      html += '</div></a></div>';
    });
    html += '</div></div>';
    return html;
  }

  function mergePredictiveHtml(predictiveHtml, skuProducts, query) {
    if (!skuProducts.length) return predictiveHtml;

    if (!predictiveHtml) {
      return (
        '<div class="predictive-search__results-wrapper">' +
          '<div class="predictive-search__results wrapper-spacing--h">' +
            buildPredictiveSkuGrid(skuProducts, query) +
            '<div class="predictive-search__view-all-mobile text-link-animated">' +
              '<a href="' + root() + 'search?q=' + encodeURIComponent(query) + '&type=product" class="predictive-search__list-item">View all</a>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    var doc = new DOMParser().parseFromString(predictiveHtml, 'text/html');
    var wrapper = doc.querySelector('.predictive-search__results-wrapper');
    if (!wrapper) return predictiveHtml + buildPredictiveSkuGrid(skuProducts, query);

    var productsGrid = wrapper.querySelector('[data-grid-id="products"]');
    if (productsGrid) productsGrid.remove();

    var mainResults = wrapper.querySelector('.predictive-search__main-results');
    var results = wrapper.querySelector('.predictive-search__results');
    var skuBlock = buildPredictiveSkuGrid(skuProducts, query);

    if (mainResults) {
      mainResults.insertAdjacentHTML('afterbegin', skuBlock);
    } else if (results) {
      results.insertAdjacentHTML('afterbegin', skuBlock);
    } else {
      wrapper.insertAdjacentHTML('afterbegin', skuBlock);
    }

    var noResults = wrapper.querySelector('.predictive-search__no-results');
    if (noResults) noResults.remove();

    return wrapper.outerHTML;
  }

  function collectCatalogHandles() {
    var handles = {};
    document.querySelectorAll('[data-search-catalog-handle]').forEach(function(el) {
      var handle = String(el.getAttribute('data-search-catalog-handle') || '').trim();
      if (handle) handles[handle] = true;
    });
    if (!Object.keys(handles).length) handles['all-jewelry'] = true;
    return Object.keys(handles);
  }

  function preloadCatalogs() {
    collectCatalogHandles().forEach(function(handle) {
      loadCatalog(handle);
    });
  }

  function preloadCatalog(handle) {
    return loadCatalog(resolveCatalogHandle(handle));
  }

  function bindSkuSearchForms() {
    document.querySelectorAll('form[action*="/search"], form.search-bar, form.search-page-bar__form').forEach(function(form) {
      if (form.dataset.skuPrefixBound === 'true') return;
      form.dataset.skuPrefixBound = 'true';
      form.addEventListener('submit', function() {
        var input = form.querySelector('input[name="q"], input[type="search"]');
        if (!input) return;
        var raw = (input.value || '').trim();
        if (raw.length < MIN_CHARS) return;
        if (/^[a-z0-9]/i.test(raw)) {
          input.value = shopifySkuSearchQuery(raw);
        }
      });
    });
  }

  window.SearchSkuSuggestions = {
    MIN_CHARS: MIN_CHARS,
    search: searchProducts,
    searchSku: searchSkuOnly,
    searchAll: searchAllProducts,
    preload: preloadCatalog,
    primaryQuery: primaryQuery,
    digitCore: digitCore,
    expandSkuQueries: expandSkuQueries,
    shopifySkuSearchQuery: shopifySkuSearchQuery,
    buildSpsProductItems: buildSpsProductItems,
    mergeSpsDropdown: mergeSpsDropdown,
    mergePredictiveHtml: mergePredictiveHtml
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      preloadCatalogs();
      bindSkuSearchForms();
    });
  } else {
    preloadCatalogs();
    bindSkuSearchForms();
  }
  document.addEventListener('shopify:section:load', bindSkuSearchForms);
})();
